import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { whenDisplayFontReady } from "../motion/reveals";

gsap.registerPlugin(ScrollTrigger);

/**
 * THE CREED — the artistic, no-CTA statement section.
 * Client pick: GLIDE (review option C).
 *
 * A horizontal strip [text][photo] x3 slides left 1:1 with scroll while the
 * viewport-height stage stays put.
 *
 * IMPLEMENTATION RULE (see /app/memory/HERO_WORDMARK_AUDIT.md § pinning):
 *   NO GSAP `pin`. Pinning swaps the section to position:fixed at the pin
 *   boundary, which in Safari produced a visible snap on entry, a stage that
 *   did not fill the viewport (the marquee showed underneath) and the old
 *   story section's "re-snap". Instead:
 *   - the outer section is tall: height = 100vh + glide distance (CSS var)
 *   - the stage inside is `position: sticky; top: 0; height: 100vh`
 *   - a scrub tween (no pin) maps the section's own scroll progress to the
 *     strip's translateX. Native sticky = no boundary swap, no spacer.
 *
 * Robustness: distance is re-measured on resize / font load / image sizing
 * (ResizeObserver) and pushed to the CSS var BEFORE ScrollTrigger.refresh();
 * reduced motion → static vertical stack. Copy is placeholder; photos are
 * daytime paddock stand-ins.
 */
// Client-supplied lines (2026-10-10, replaces the 2026-10-07 copy). Do not
// paraphrase. No full stops. Each line is a list of segments; `hi: true`
// segments render in brand yellow (the words the client put in quotes).
export const CREED_LINES = [
  [{ t: "Built by motorsports enthusiasts", hi: true }, { t: " for motorsports enthusiasts" }],
  [{ t: "An obsession with details", hi: true }, { t: " separates good events from great ones" }],
  [{ t: "We exist to " }, { t: "raise the standard", hi: true }, { t: " of automotive events" }],
];
export const creedText = (l) => (typeof l === "string" ? l : l.map((s) => s.t).join(""));
// Grade (desaturate/contrast) is BAKED into these files — no runtime CSS
// filter, so the browser composites plain image layers. (A filter on three
// viewport-sized moving layers was the main cost vs the mockup.)
export const CREED_PHOTOS = [
  // Client photography (Sets 3-4, 2026-09-15). Mono grade baked in, matching the rails.
  "/photos/creed-2-gt2rs-crew.webp",
  "/photos/creed-1-night-grid.webp",
  "/photos/creed-3-briefing-tee.webp",
];

export default function Creed() {
  const ref = useRef(null);
  const [phone, setPhone] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 700px)").matches);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 700px)");
    const onChange = (e) => setPhone(e.matches);
    mq.addEventListener ? mq.addEventListener("change", onChange) : mq.addListener(onChange);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", onChange) : mq.removeListener(onChange));
  }, []);

  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // Phones: a horizontal glide on a ~390px stage only ever shows a slice of
    // each statement, so below 700px the section is the readable vertical
    // stack (same CSS as reduced motion). `phone` state re-runs this effect.
    if (reduce || phone) {
      root.classList.add("is-static");
      return () => root.classList.remove("is-static");
    }

    const strip = root.querySelector("[data-creed-strip]");
    const stage = root.querySelector("[data-creed-stage]");
    if (!strip || !stage) return undefined;

    let ctx = null;
    let disposed = false;
    let raf = 0;
    const timers = [];

    // Glide distance = how far the strip overflows the stage.
    const measure = () => Math.max(0, Math.round(strip.scrollWidth - stage.clientWidth));
    // Dwell: the stage sits fully in frame and still for LEAD px of scroll
    // before the strip moves, and for TAIL px after it stops, so the switch
    // from vertical to horizontal motion never happens at the section edge.
    const lead = () => Math.round(window.innerHeight * 0.45);
    const tail = () => Math.round(window.innerHeight * 0.25);
    const applyDistance = () => {
      const d = measure();
      root.style.setProperty("--creed-dist", `${d}px`);
      root.style.setProperty("--creed-lead", `${lead()}px`);
      root.style.setProperty("--creed-tail", `${tail()}px`);
      root.classList.toggle("is-static", d <= 0);
      return d;
    };

    const build = () => {
      if (disposed) return;
      ctx?.revert();
      if (applyDistance() <= 0) return;
      ctx = gsap.context(() => {
        // ONE moving layer. The strip translates 1:1 with scroll between the
        // lead and tail dwells (ease none, scrub true). No per-photo parallax:
        // three extra viewport-sized transforms per frame is what made the live
        // version feel less buttery than the mockup.
        gsap.fromTo(strip, { x: 0 }, {
          x: () => -measure(),
          ease: "none",
          force3D: true,
          scrollTrigger: {
            trigger: root,
            start: () => `top+=${lead()} top`,
            end: () => `bottom-=${tail()} bottom`,
            // Numeric scrub = a short smoothing window. Safari delivers scroll
            // events irregularly during trackpad momentum decay; with `true`
            // the strip copies that jitter 1:1 in the deceleration tail. 0.35s
            // lets GSAP interpolate toward the target every frame instead.
            scrub: 0.35,
            invalidateOnRefresh: true,
          },
        });
      }, root);
      ScrollTrigger.refresh();
    };

    // Re-measure whenever the strip's size changes (fonts, images, viewport).
    const ro = "ResizeObserver" in window
      ? new ResizeObserver(() => {
          if (disposed) return;
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => {
            const before = root.style.getPropertyValue("--creed-dist");
            applyDistance();
            if (root.style.getPropertyValue("--creed-dist") !== before) ScrollTrigger.refresh();
          });
        })
      : null;
    ro?.observe(strip);
    ro?.observe(stage);

    whenDisplayFontReady().then(() => requestAnimationFrame(build));
    timers.push(setTimeout(() => { if (!disposed) { applyDistance(); ScrollTrigger.refresh(); } }, 900));
    timers.push(setTimeout(() => { if (!disposed) { applyDistance(); ScrollTrigger.refresh(); } }, 2200));

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
      ro?.disconnect();
      ctx?.revert();
      root.style.removeProperty("--creed-dist");
      root.style.removeProperty("--creed-lead");
      root.style.removeProperty("--creed-tail");
      root.classList.remove("is-static");
    };
  }, [phone]);

  return (
    <section className="gm-creed" id="creed" ref={ref} data-testid="creed-section" aria-label="The Creed">
      <div className="gm-creed__stage" data-creed-stage>
        <div className="gm-creed__strip" data-creed-strip>
          {CREED_LINES.map((t, i) => (
            <div className="gm-creed__pair" key={i}>
              <div className="gm-creed__text">
                <p className="display-md gm-creed__line" data-testid={`creed-line-${i}`}>
                  {typeof t === "string"
                    ? t
                    : t.map((seg, k) => (seg.hi
                      ? <span className="gm-creed__hi" key={k} data-testid={`creed-line-${i}-hi`}>{seg.t}</span>
                      : <span key={k}>{seg.t}</span>))}
                </p>
              </div>
              <div className="gm-creed__photo" data-creed-photo>
                <img src={CREED_PHOTOS[i]} alt="" loading="eager" decoding="async" fetchPriority={i === 0 ? "high" : "auto"} onError={(e) => (e.currentTarget.style.visibility = "hidden")} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
